import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "../LanguageContext";
import { Calendar } from "./ui/calendar";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { normalizePhone, isValidE164Phone } from "../lib/phone";
import { generateUuid } from "../lib/uuid";

const empty = { name: "", phone: "", email: "", telegram: "", whatsapp: "", consent: false, honeypot: "" };

export const ContactForm = () => {
  const { t } = useLang();
  const f = t.form;
  
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty);
  const [prefDate, setPrefDate] = useState(null);
  const [prefTime, setPrefTime] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const [turnstileToken, setTurnstileToken] = useState(null);
  const [submissionId, setSubmissionId] = useState(() => generateUuid());
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const resetTurnstile = () => {
    setTurnstileToken(null);
    if (widgetIdRef.current !== null && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {}
    }
  };

  const resetFormState = () => {
    setDone(false);
    setStep(1);
    setForm(empty);
    setPrefDate(null);
    setPrefTime("");
    setTurnstileToken(null);
    setSubmissionId(generateUuid());
    if (widgetIdRef.current !== null && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {}
    }
  };

  useEffect(() => {
    let isMounted = true;
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
    if (!siteKey || step !== 2) return;

    const scriptId = "cf-turnstile-script";

    const renderWidget = () => {
      if (!isMounted || !containerRef.current || !window.turnstile) return;
      if (widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: "dron_lead_submit",
          theme: "dark",
          callback: (token) => {
            if (isMounted) setTurnstileToken(token);
          },
          "expired-callback": () => {
            if (isMounted) setTurnstileToken(null);
          },
          "error-callback": () => {
            if (isMounted) setTurnstileToken(null);
          },
        });
      } catch (e) {
        console.error("Turnstile render error", e);
      }
    };

    const handleScriptLoad = () => {
      renderWidget();
    };

    let loadedScript = null;
    if (window.turnstile) {
      renderWidget();
    } else {
      let script = document.getElementById(scriptId);
      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      loadedScript = script;
      script.addEventListener("load", handleScriptLoad);
    }

    return () => {
      isMounted = false;
      if (loadedScript) {
        loadedScript.removeEventListener("load", handleScriptLoad);
      }
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [step]);

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = f.errors.name;
    const hasPhone = form.phone && isValidE164Phone(form.phone);
    const hasEmail = form.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());

    const hasTelegram = Boolean(form.telegram && form.telegram.trim());

    if (!hasPhone && !hasEmail && !hasTelegram) e.contact = f.errors.contact;
    if (form.phone && !hasPhone) e.phone = f.errors.phone;
    if (form.email && !hasEmail) e.email = f.errors.email;
    if (!form.consent) e.consent = f.errors.consent;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    if (ev) ev.preventDefault();
    setLoading(true);
    
    const finalDateStr = prefDate ? format(prefDate, "yyyy-MM-dd", { locale: pl }) : null;
    const finalDate = finalDateStr || (prefTime && !prefDate ? "Sugerowany termin" : null);
    const finalTime = prefTime;

    try {
      await axios.post("/api/leads", {
        name: form.name.trim(),
        phone: form.phone.trim() ? normalizePhone(form.phone) : "",
        email: form.email.trim() || "",
        telegram: form.telegram.trim() || "",
        whatsapp: form.whatsapp.trim() || "",
        consent: form.consent,
        preferred_date: finalDate || "",
        preferred_time: finalTime || "",
        turnstileToken: turnstileToken || "",
        honeypot: form.honeypot || "",
        submission_id: submissionId,
      });
      setDone(true);
      setStep(1);
      setForm(empty);
      setPrefDate(null);
      setPrefTime("");
      setTurnstileToken(null);
      setSubmissionId(generateUuid());
      toast.success(f.successToast);
    } catch (err) {
      resetTurnstile();
      toast.error(f.errors.generic);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-black border border-white/20 focus:border-[#E02020] focus:outline-none text-white placeholder-white/30 px-4 py-3 rounded-sm transition-colors";

  return (
    <section id="formularz" className="bg-[#0F0F0F] py-20 sm:py-28 border-t border-white/10">
      <div className="max-w-2xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-10">
          <p className="font-body text-xs font-bold uppercase tracking-[0.22em] text-[#E02020] mb-4">{f.overline}</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            {step === 1 ? f.title : f.scheduleTitle}
          </h2>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            data-testid="form-success"
            className="bg-[#1A1A1A] border border-[#E02020]/40 rounded-sm p-10 text-center"
          >
            <PartyPopper className="text-[#E02020] mx-auto mb-4" size={40} />
            <h3 className="font-display text-2xl font-semibold text-white mb-2">{f.successTitle}</h3>
            <p className="text-[#A3A3A3]">{f.successText}</p>
            <button
              data-testid="form-reset"
              onClick={resetFormState}
              className="mt-6 text-sm text-white underline underline-offset-4 hover:text-[#E02020]"
            >
              {f.successAgain}
            </button>
          </motion.div>
        ) : step === 1 ? (
          <form 
            onSubmit={(e) => { e.preventDefault(); if (validate()) setStep(2); }} 
            data-testid="lead-form" 
            className="bg-[#1A1A1A] border border-white/10 rounded-sm p-6 sm:p-8 space-y-4"
          >
            <input
              type="text"
              name="website_check"
              tabIndex={-1}
              autoComplete="off"
              value={form.honeypot}
              onChange={(e) => set("honeypot", e.target.value)}
              style={{ position: "absolute", left: "-9999px", opacity: 0 }}
            />

            <div>
              <label className="block text-sm text-white/80 mb-1.5">{f.name} *</label>
              <input data-testid="input-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={f.placeholders.name} />
              {errors.name && <p className="text-[#E02020] text-xs mt-1" data-testid="error-name">{errors.name}</p>}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-white/80 mb-1.5">{f.phone} *</label>
                <input data-testid="input-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder={f.placeholders.phone} inputMode="tel" />
                {errors.phone && <p className="text-[#E02020] text-xs mt-1" data-testid="error-phone">{errors.phone}</p>}
              </div>
              <div>
                <label className="block text-sm text-white/80 mb-1.5">{f.email} *</label>
                <input data-testid="input-email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder={f.placeholders.email} inputMode="email" />
                {errors.email && <p className="text-[#E02020] text-xs mt-1" data-testid="error-email">{errors.email}</p>}
              </div>
            </div>
            <p className="text-xs text-white/40 -mt-1">{f.required}</p>
            {errors.contact && <p className="text-[#E02020] text-xs" data-testid="error-contact">{errors.contact}</p>}

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-white/80 mb-1.5">{f.telegram}</label>
                <input data-testid="input-telegram" className={inputCls} value={form.telegram} onChange={(e) => set("telegram", e.target.value)} placeholder={f.placeholders.telegram} />
              </div>
              <div>
                <label className="block text-sm text-white/80 mb-1.5">{f.whatsapp}</label>
                <input data-testid="input-whatsapp" className={inputCls} value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder={f.placeholders.whatsapp} />
              </div>
            </div>

            <label className="flex items-start gap-3 text-sm text-white/70 pt-1 cursor-pointer">
              <input type="checkbox" data-testid="input-consent" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1 w-4 h-4 accent-[#E02020]" />
              <span>
                {f.consentPre}
                <Link to="/polityka-prywatnosci" className="text-white underline underline-offset-2 hover:text-[#E02020]">{f.consentLink}</Link>.
              </span>
            </label>
            {errors.consent && <p className="text-[#E02020] text-xs" data-testid="error-consent">{errors.consent}</p>}

            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#E02020] hover:bg-[#B91818] text-white font-medium text-base px-8 py-4 rounded-sm transition-colors mt-4"
            >
              {f.scheduleButton}
            </button>
          </form>
        ) : (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-[#1A1A1A] border border-white/10 rounded-sm p-6 sm:p-8 space-y-6"
          >
            <div className="bg-orange-500/10 text-orange-400 p-4 rounded-sm border border-orange-500/20 text-sm">
              <p className="font-semibold mb-1">{f.scheduleInfoTitle}</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                {f.scheduleInfoItems.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>

            <div>
              <label className="block text-sm font-semibold text-white mb-3">{f.groupDatesTitle}</label>
              <div className="grid sm:grid-cols-2 gap-3">
                {f.scheduleSlots.map((slot) => (
                   <button 
                     key={slot}
                     type="button"
                     onClick={() => { setPrefDate(null); setPrefTime(slot); }}
                     className={`p-3 text-sm rounded-sm border transition-colors ${prefTime === slot && !prefDate ? 'border-[#E02020] bg-[#E02020]/10 text-white' : 'border-white/20 text-white/70 hover:border-white/50'}`}
                   >
                     {slot}
                   </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-white mb-3">{f.customDateTitle}</label>
              <div className="bg-black/50 border border-white/10 p-4 rounded-sm flex flex-col sm:flex-row gap-6">
                <div className="bg-[#1a1a1a] p-2 rounded-md border border-white/10">
                  <Calendar
                    mode="single"
                    selected={prefDate}
                    onSelect={(d) => { setPrefDate(d); setPrefTime(""); }}
                    locale={pl}
                    disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                    className="pointer-events-auto text-white"
                  />
                </div>
                {prefDate && (
                  <div className="flex-1 space-y-4">
                    <label className="block text-sm text-white/80">{f.preferredTime}</label>
                    <input 
                      type="time" 
                      value={prefTime} 
                      onChange={e => setPrefTime(e.target.value)} 
                      className={inputCls} 
                    />
                  </div>
                )}
              </div>
            </div>

            {process.env.REACT_APP_TURNSTILE_SITE_KEY && (
              <div className="flex justify-center pt-2">
                <div ref={containerRef} id="cf-turnstile-container" data-testid="turnstile-container" />
              </div>
            )}

            <div className="flex gap-4 pt-4 border-t border-white/10">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="px-6 py-4 rounded-sm border border-white/20 text-white hover:bg-white/5 transition-colors font-medium"
              >
                {f.back}
              </button>
              <button
                onClick={() => submit()}
                disabled={loading || (!prefDate && !prefTime) || (Boolean(process.env.REACT_APP_TURNSTILE_SITE_KEY) && !turnstileToken)}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#E02020] hover:bg-[#B91818] disabled:opacity-60 text-white font-medium text-base px-8 py-4 rounded-sm transition-colors"
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                {f.scheduleSubmit}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};
